import DownloadButton from '@/components/Elements/Button/DownloadButton'
import { CheckboxCell, DateCell, InputCell, MultipleSelectCell, SelectCell } from '@/components/Table/components/Cell'
import DatePickerCell from '@/components/Table/components/Cell/DatePickerCell'
import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'
import { createOptions } from '@/utils/arrayHelper'
import { isNullish } from '@/utils/helper'
import { formatCurrencyAmount } from '@/utils/stringHelper'
import { Currency } from '@eapproval/offer-shared'
import { Box, Button, Input as ChakraInput, Flex, Tooltip } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

export const useCostBudgetVendorColumns = ({
  vendorCategoryOptions,
  fetchVendorResponse,
  fetchVendorUserResponse,
  handleOpenVendorDetail,
  handlePost,
  handleRemoveVendor,
  canEditVendor,
  hasDueDate,
  passedDueDate,
}) => {
  const columnHelper = createColumnHelper()

  return useMemo(
    () => [
      columnHelper.display({
        id: 'index',
        header: () => <></>,
        cell: ({ row: { index } }) => {
          return <TooltipCell text={`Vendor ${index + 1}`} />
        },
      }),
      columnHelper.accessor('category', {
        header: () => <>Vendor Category</>,
        size: 200,
        cell: (cell) => {
          const {
            getValue,
            row: { original },
            table,
          } = cell

          return (
            <SelectCell
              {...cell}
              options={vendorCategoryOptions}
              extraDisable={original?.hasNoticed}
              searchable={true}
            />
          )
        },
      }),
      columnHelper.accessor('vendor', {
        header: () => <>Supplier</>,
        size: 200,
        cell: (cell) => {
          const {
            getValue,
            row: { original },
            column: { id: columnId },
            table,
          } = cell

          const currentSelected = getValue()

          const selectedVendors = table
            .getCoreRowModel()
            .flatRows.map((row) => row.getValue(columnId))
            .filter((vendorId) => vendorId !== currentSelected)

          const filteredVendorOptions = !Array.isArray(fetchVendorResponse?.data)
            ? []
            : !original?.category || original?.category === ''
              ? createOptions(
                  fetchVendorResponse.data.filter((vendor) => !selectedVendors.includes(vendor._id)),
                  'name',
                  '_id'
                )
              : createOptions(
                  fetchVendorResponse.data.filter(
                    (vendor) => vendor.components.includes(original.category) && !selectedVendors.includes(vendor._id)
                  ),
                  'name',
                  '_id'
                )

          return (
            <SelectCell
              {...cell}
              options={filteredVendorOptions}
              extraDisable={original?.hasNoticed}
              searchable={true}
            />
          )
        },
      }),
      columnHelper.accessor('contactPersons', {
        header: () => <>Contact Person</>,
        size: 200,
        cell: (cell) => {
          const {
            row: { original },
          } = cell
          const vendorUsers = fetchVendorUserResponse?.data.filter((user) => user.vendorId === original?.vendor)

          const vendorUserOptions = createOptions(vendorUsers, 'fullName', '_id')

          return <MultipleSelectCell {...cell} options={vendorUserOptions} extraDisable={original?.hasNoticed} />
        },
      }),
      columnHelper.accessor('email', {
        size: 230,
        cell: ({ row: { original } }) => {
          const contactPersons = original?.contactPersons
          if (
            !Array.isArray(fetchVendorUserResponse?.data) ||
            !Array.isArray(contactPersons) ||
            contactPersons.length === 0
          ) {
            return <></>
          }

          const selectedUser = fetchVendorUserResponse.data.filter((user) => contactPersons.includes(user._id))

          return <>{selectedUser.map((user) => user.email).join(', ')}</>
        },
      }),
      columnHelper.accessor('mainContactNumber', {
        header: () => <>Contact #</>,
        size: 200,
        cell: InputCell,
      }),
      columnHelper.accessor('tenderDocumentIndex', {
        header: () => <>Tender Version</>,
        size: 100,
        cell: ({ row: { original } }) => {
          const history = original?.history

          if (!history || history?.length === 0) {
            return <TooltipCell text={'0'} />
          }

          const lastQuote = history[history.length - 1]

          return <TooltipCell text={`${lastQuote.tenderDocumentIndex}`} />
        },
      }),
      columnHelper.accessor('history', {
        header: () => <>Newest Quote</>,
        size: 120,
        cell: ({ getValue }) => {
          let latestQuote = 0
          let latestCurrency = ''
          const history = getValue()

          if (history?.length > 0) {
            latestQuote = history[history.length - 1]?.budget
            latestCurrency = history[history.length - 1]?.currency ?? Currency.HKD
          }

          return <TooltipCell text={formatCurrencyAmount(latestQuote, latestCurrency)} />
        },
      }),
      columnHelper.display({
        id: 'currency',
        size: 100,
        header: () => <>Currency</>,
        cell: ({ row: { original } }) => {
          const history = original?.history
          if (!history || history?.length === 0) {
            return <TooltipCell text={'-'} />
          }
          const lastQuote = history[history.length - 1]

          return <TooltipCell text={lastQuote?.currency ?? Currency.HKD} />
        },
      }),
      columnHelper.accessor('history', {
        header: () => <>Newest Quote</>,
        size: 120,
        cell: ({ getValue }) => {
          let latestQuote = 0
          let latestCurrency = ''
          const history = getValue()

          if (history?.length > 0) {
            latestQuote = history[history.length - 1]?.budget
            latestCurrency = history[history.length - 1]?.currency ?? Currency.HKD
          }

          // return <TooltipCell text={formatCurrencyAmount(latestQuote, latestCurrency)} />
          return <TooltipCell text={formatCurrencyAmount(latestQuote)} />
        },
      }),
      columnHelper.accessor('creditTerm', {
        size: 100,

        cell: ({ getValue, row: { original, index }, column: { id: columnId }, table }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const [localValue, setLocalValue] = useState(!isNullish(getValue()) ? parseInt(getValue()) : 90)
          const isDisabled = table.options.meta?.disabled

          const onChange = ({ target: { value } }) => {
            if (!isNullish(value)) {
              setLocalValue(parseInt(value))
            } else {
              setLocalValue(0)
            }
          }

          const onBlur = () => {
            table.options.meta?.updateData(index, columnId, localValue)
          }

          return (
            <Box>
              <Tooltip
                label={`Payment Term: ${
                  isNullish(original?.paymentTerm) || original?.paymentTerm === 'undefined'
                    ? '(empty)'
                    : original.paymentTerm
                }`}
              >
                <ChakraInput
                  type="number"
                  value={localValue}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={isDisabled}
                />
              </Tooltip>
            </Box>
          )
        },
      }),
      columnHelper.accessor('status', {
        size: 100,
      }),
      columnHelper.accessor('dueDate', {
        size: 150,

        cell: DatePickerCell,
      }),
      columnHelper.accessor('isExistingVendor', {
        size: 100,
        header: () => <>Existing Vendor</>,
        cell: CheckboxCell,
      }),
      columnHelper.display({
        id: 'action',
        header: () => <>Action</>,
        size: 350,
        cell: ({ row: { index, original } }) => {
          const history = original?.history
          const hasQuote = original?.history.length > 0
          let latestFileId = ''
          let latestFilename = ''

          if (history && history.length > 0) {
            const lastHistory = history[history.length - 1]

            if (lastHistory?.files && lastHistory?.files.length > 0) {
              latestFileId = lastHistory?.files[0]?._id || ''
              latestFilename = lastHistory?.files[0]?.originalFileName || ''
            }
          }

          const hasUser = Array.isArray(original?.contactPersons) && original?.contactPersons.length > 0

          return (
            <Flex gap={2} flexWrap="wrap">
              <Button onClick={() => handleOpenVendorDetail(index)}>Detail</Button>
              {canEditVendor && hasUser && (
                <Button colorScheme="green" onClick={() => handlePost(index)}>
                  {hasDueDate && !passedDueDate && hasQuote ? 'Request New Quote' : 'POST'}
                </Button>
              )}

              <DownloadButton fileId={latestFileId} fileName={latestFilename} />
              {canEditVendor && index !== 0 && history.length === 0 && (
                <Button colorScheme={'red'} onClick={() => handleRemoveVendor(index)}>
                  Remove
                </Button>
              )}
            </Flex>
          )
        },
      }),
    ],
    [
      columnHelper,
      fetchVendorResponse?.data,
      fetchVendorUserResponse?.data,
      handleOpenVendorDetail,
      handlePost,
      handleRemoveVendor,
      canEditVendor,
      hasDueDate,
      passedDueDate,
      vendorCategoryOptions,
    ]
  )
}
